import { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { ShieldCheck, FileText, Trash2, PenLine, CheckCircle2, Upload, Eye, Clock, AlertCircle, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useFileDrop } from "@/lib/useFileDrop";

export function JobSSSP({ jobId }) {
  const { user } = useAuth();
  const canManage = user.role !== "field";
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [signDoc, setSignDoc] = useState(null);

  const load = useCallback(() => {
    api.get(`/job-sssp/${jobId}/docs`).then((r) => setDocs(r.data.docs || [])).catch(() => setDocs([]));
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const tok = () => localStorage.getItem("fl_token") || "";
  const fileUrl = (path, download) => `${API}/files/view?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(tok())}${download ? "&download=1" : ""}`;

  const handleFiles = async (fileList) => {
    const file = (fileList || [])[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/job-sssp/${jobId}/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("SSSP uploaded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const { dragging, dropProps } = useFileDrop(handleFiles, { disabled: busy });

  const removeDoc = async (d) => {
    if (!window.confirm(`Remove "${d.name}"? Sign-offs will be hidden.`)) return;
    try { await api.delete(`/job-sssp/docs/${d.id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const onSign = async (dataUrl) => {
    if (!signDoc) return;
    try {
      await api.post(`/job-sssp/docs/${signDoc.id}/sign`, { signature: dataUrl });
      toast.success("Signed — thanks");
      setSignDoc(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save signature"); }
  };

  const downloadRecord = async (d) => {
    try {
      const res = await api.get(`/job-sssp/docs/${d.id}/record.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `SSSP-Signed-${(d.name || "record").replace(/[^A-Za-z0-9_-]+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Signed record generated & filed to the job");
    } catch { toast.error("Could not generate record"); }
  };

  return (
    <section className="hard-card p-5" data-testid="job-sssp-panel">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="label-tag flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Site-specific safety plan (SSSP)
        </div>
        {canManage && (
          <label className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 border-dashed text-[11px] font-bold uppercase tracking-wider cursor-pointer transition-colors ${busy ? "opacity-50 pointer-events-none border-zinc-400" : dragging ? "border-[#FF5F15] bg-orange-50 text-[#FF5F15]" : "border-zinc-400 hover:border-[#FF5F15] hover:text-[#FF5F15]"}`}
            {...dropProps} data-testid="sssp-upload">
            <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : dragging ? "Drop SSSP" : "Drag & drop or upload SSSP"}
            <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" className="hidden" disabled={busy} onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
          </label>
        )}
      </div>

      {docs.length === 0 ? (
        <p className="text-sm text-zinc-500">{canManage ? "Upload your company's SSSP for this job so the crew can read and sign off." : "No SSSP has been uploaded for this job yet."}</p>
      ) : (
        <div className="space-y-3">
          {docs.map((d) => (
            <div key={d.id} className="border border-zinc-200 dark:border-white/10 rounded-lg p-3" data-testid={`sssp-doc-${d.id}`}>
              <div className="flex items-center gap-2.5 flex-wrap">
                <FileText className="w-5 h-5 text-[#FF5F15] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm text-zinc-900 dark:text-white truncate">{d.name}</div>
                  <div className="text-[11px] text-zinc-500">Uploaded by {d.uploaded_by_name} · {(d.uploaded_at || "").slice(0, 10)}</div>
                </div>
                {d.my_signed
                  ? <Badge className="bg-emerald-500/15 text-emerald-600 gap-1"><CheckCircle2 className="w-3 h-3" /> You signed</Badge>
                  : <Badge variant="outline" className="text-amber-600 border-amber-400">Not signed</Badge>}
                <a href={fileUrl(d.path)} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 hover:text-[#FF5F15]"
                   data-testid={`sssp-view-${d.id}`}>
                  <Eye className="w-3.5 h-3.5" /> View
                </a>
                {!d.my_signed && (
                  <Button size="sm" onClick={() => setSignDoc(d)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-[11px] font-bold tracking-wider h-8" data-testid={`sssp-sign-${d.id}`}>
                    <PenLine className="w-3.5 h-3.5 mr-1" /> Read &amp; sign
                  </Button>
                )}
                {canManage && (
                  <button onClick={() => removeDoc(d)} className="text-zinc-400 hover:text-red-600" data-testid={`sssp-del-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
              </div>

              {canManage && d.signed_count > 0 && (
                <div className="mt-2">
                  <button onClick={() => downloadRecord(d)}
                          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 hover:text-[#FF5F15]"
                          data-testid={`sssp-record-${d.id}`}>
                    <FileDown className="w-3.5 h-3.5" /> Signed record (PDF) · filed to job
                  </button>
                </div>
              )}

              <div className="mt-3 pt-2 border-t border-zinc-100 dark:border-white/5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-1.5">
                  Signed off · {d.signed_count}{typeof d.assigned_count === "number" ? ` of ${d.assigned_count}` : ""}
                </div>
                {d.signatures.length === 0 ? (
                  <p className="text-xs text-zinc-500">No one has signed yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {d.signatures.map((s) => (
                      <div key={s.user_id} className="flex items-center gap-1.5 text-xs bg-zinc-50 dark:bg-white/5 rounded-full pl-1 pr-2.5 py-1" data-testid={`sssp-signer-${d.id}-${s.user_id}`}>
                        {s.signature_path
                          ? <img src={fileUrl(s.signature_path)} alt="" className="w-8 h-5 object-contain bg-white rounded-sm border border-zinc-200" />
                          : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                        <span className="font-medium text-zinc-800 dark:text-zinc-200">{s.user_name}</span>
                        <span className="text-zinc-400">{(s.signed_at || "").slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {canManage && Array.isArray(d.pending) && (
                  <div className="mt-3" data-testid={`sssp-pending-block-${d.id}`}>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-amber-600 font-bold mb-1.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Not yet signed · {d.pending_count ?? d.pending.length}
                    </div>
                    {d.pending.length === 0 ? (
                      <p className="text-xs text-emerald-600 font-medium">
                        {typeof d.assigned_count === "number" && d.assigned_count === 0
                          ? "No crew dispatched to this job yet."
                          : "Everyone dispatched to this job has signed."}
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {d.pending.map((p) => (
                          <div key={p.user_id} className="flex items-center gap-1.5 text-xs bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-full px-2.5 py-1 border border-amber-200 dark:border-amber-500/20" data-testid={`sssp-pending-${d.id}-${p.user_id}`}>
                            <AlertCircle className="w-3.5 h-3.5" />
                            <span className="font-medium">{p.user_name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!signDoc} onOpenChange={(v) => !v && setSignDoc(null)}>
        <DialogContent data-testid="sssp-sign-dialog">
          <DialogHeader><DialogTitle className="uppercase font-display text-xl">Sign off — {signDoc?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 -mt-1">
            Please read the SSSP first, then sign below to confirm you understand it.
          </p>
          {signDoc && (
            <a href={fileUrl(signDoc.path)} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FF5F15] hover:underline" data-testid="sssp-sign-view">
              <Eye className="w-4 h-4" /> Open the SSSP to read
            </a>
          )}
          <div className="mt-2">
            <SignaturePad testIdPrefix="sssp-sig" onSave={onSign} onCancel={() => setSignDoc(null)} />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
